// Type-to-confirm dialog for the destructive Reset (truncate preview data).
// The user must type the object's exact name to enable the confirm button,
// so a stray click can't wipe the data. Surfaced from the Live Preview tab's
// deploy 3-dot menu (below "Deployment status").

import { useState } from 'react'
import { Trash2 } from 'lucide-react'

export default function ResetConfirmModal({
  name, busy = false, onCancel, onConfirm,
}: {
  name: string
  busy?: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const [typed, setTyped] = useState('')
  const matches = typed.trim() === name
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 9500,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(440px, 92vw)', background: 'var(--color-bg)',
          border: '1px solid var(--color-border)', borderRadius: 10,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: 20,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Trash2 size={18} style={{ color: 'var(--color-error, #ef4444)' }} />
          <strong style={{ fontSize: '1.0125rem' }}>Reset preview data</strong>
        </div>
        <p style={{ margin: 0, fontSize: '0.8825rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          This permanently deletes <strong>all records</strong> for this object
          from the preview database. The table itself is kept. This cannot be
          undone.
        </p>
        <label style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)' }}>
          Type <code style={{ color: 'var(--color-text)', fontWeight: 700 }}>{name}</code> to confirm:
        </label>
        <input
          autoFocus
          type="text"
          value={typed}
          placeholder={name}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && matches && !busy) onConfirm() }}
          style={{
            padding: '8px 10px', borderRadius: 6, fontSize: '0.9125rem',
            border: '1px solid var(--color-border)', background: 'var(--lp-input-bg, var(--color-bg))',
            color: 'var(--color-text)', outline: 'none',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn btn-sm"
            onClick={onConfirm}
            disabled={!matches || busy}
            style={{
              background: matches ? 'var(--color-error, #ef4444)' : 'var(--color-bg-secondary)',
              color: matches ? '#fff' : 'var(--color-text-secondary)',
              border: 'none', cursor: matches && !busy ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Resetting…' : 'Reset data'}
          </button>
        </div>
      </div>
    </div>
  )
}
